// ============================================================================
// UNIVERSAL ACTION HANDLERS - DEAL-SPECIFIC ACTIONS
// ============================================================================

import { MODAL_IDS } from "@west-tennessee-consulting/ui";

import type { UniversalActionHandlers } from "./types";
import type { Deal_Stage } from "@/src/gql/graphql";
import {
  extractCompanyId,
  extractCompanyName,
} from "@/utils/entity-type-guards";

/**
 * Deal-Specific Actions (stage changes, follow-up tagging, stage updates)
 */
export const dealActions: UniversalActionHandlers = {
  /**
   * Log a new deal — opens CreateDealModal pre-filled with company context.
   *
   * Invoked when the cross-entity resolver is bypassed (source entity already
   * IS a company). Resolves company id/name from the row and opens the modal.
   * Contact-source invocations go through the resolver and `clientRun` in
   * `lib/actions/manifests/logDeal.ts` instead.
   */
  openCreateDealModal: async (item, context) => {
    const row = item as Record<string, unknown>;
    const entityName = context?.pageConfig?.entityName;

    const companyId =
      context?.entityIds?.companyId ??
      extractCompanyId(row) ??
      (entityName === "companies" && typeof row.id === "string" ? row.id : undefined);

    const companyName =
      extractCompanyName(row) ??
      (entityName === "companies" && typeof row.name === "string" ? row.name : undefined);

    if (!companyId) {
      context.onToast("Cannot log deal — company could not be resolved", "warning");
      return;
    }

    context.onOpenModal(MODAL_IDS.deals.create, { companyId, companyName });
  },

  // Deal-specific actions
  changeStage: async (item, context) => {
    // Open modal to select new stage
    context.onOpenModal("change-deal-stage", {
      dealId: item.id,
      currentStage: item.stage,
      item,
    });
  },

  markWon: async (item, context) => {
    // Open modal to confirm marking deal as won. Pass current notes so the win
    // note is appended (never clobbered) — the modal is global + context-free.
    context.onOpenModal("mark-deal-won", {
      dealId: item.id,
      currentNotes: (item as { notes?: string | null }).notes ?? null,
    });
  },

  markLost: async (item, context) => {
    // Open modal to confirm marking deal as lost. Pass current notes for append.
    context.onOpenModal("mark-deal-lost", {
      dealId: item.id,
      currentNotes: (item as { notes?: string | null }).notes ?? null,
    });
  },

  // Follow-up tagging actions
  markFollowedUpThisWeek: async (item, context) => {
    try {
      const { addFollowUpTagThisWeek } = await import("@/lib/utils/follow-up-tagging");
      const { getBrowserSupabaseClient } = await import("@west-tennessee-consulting/supabase");

      const updatedTags = addFollowUpTagThisWeek(item.tags);
      const supabase = getBrowserSupabaseClient();

      // Update the deal directly via Supabase
      const { error } = await supabase
        .from("deals")
        .update({ tags: updatedTags })
        .eq("id", item.id);

      if (error) {
        throw error;
      }

      context.onToast("Deal marked as followed up this week", "success");
      void context.onRefresh?.();
    } catch (error) {
      context.onToast("Failed to mark deal as followed up", "error");
      console.error("Error marking deal as followed up:", error);
    }
  },

  unmarkFollowedUpThisWeek: async (item, context) => {
    try {
      const { removeFollowUpTagThisWeek } = await import("@/lib/utils/follow-up-tagging");
      const { getBrowserSupabaseClient } = await import("@west-tennessee-consulting/supabase");

      const updatedTags = removeFollowUpTagThisWeek(item.tags);
      const supabase = getBrowserSupabaseClient();

      // Update the deal directly via Supabase
      const { error } = await supabase
        .from("deals")
        .update({ tags: updatedTags })
        .eq("id", item.id);

      if (error) {
        throw error;
      }

      context.onToast("Follow-up tag removed", "success");
      void context.onRefresh?.();
    } catch (error) {
      context.onToast("Failed to remove follow-up tag", "error");
      console.error("Error removing follow-up tag:", error);
    }
  },

  // Snooze actions — silence the weekly follow-up reminder for chosen weeks.
  // Mirrors the follow-up tagging strategy (snooze tags live in deals.tags).
  snoozeDeal: async (item, context) => {
    context.onOpenModal(MODAL_IDS.deals.snooze, {
      dealId: item.id,
      dealTitle: (item as { title?: string | null }).title ?? null,
      tags: (item as { tags?: string[] | null }).tags ?? [],
    });
  },

  // Request a quote from the technical team. Opens a modal that collects an
  // optional note; all deal context is pulled server-side from the deal itself.
  // The created task is assigned to the technical team, due in 2 days — the same
  // shared path the Slack "Request quote" button uses.
  requestQuote: async (item, context) => {
    context.onOpenModal(MODAL_IDS.deals.requestQuote, {
      dealId: item.id,
      dealTitle: (item as { title?: string | null }).title ?? null,
    });
  },

  // Make project channel — spin up the deal's Slack project channel (same flow
  // as the Slack "Make project channel" modal). The modal collects a slug; the
  // bound onConfirm calls the employee API. Context-free modal (data payload).
  makeProjectChannel: async (item, context) => {
    context.onOpenModal(MODAL_IDS.deals.makeProjectChannel, {
      dealId: item.id,
      dealTitle: (item as { title?: string | null }).title ?? undefined,
      onConfirm: async (slug: string) => {
        try {
          const res = await fetch(`/api/projects/${item.id}/channel`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ slug }),
          });
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
            alreadyExisted?: boolean;
          };
          if (!res.ok) {
            return {
              ok: false as const,
              error: typeof body.error === "string" ? body.error : "Failed to create channel",
            };
          }
          void context.onRefresh?.();
          return { ok: true as const, alreadyExisted: Boolean(body.alreadyExisted) };
        } catch {
          return { ok: false as const, error: "Failed to create channel" };
        }
      },
    });
  },

  unsnoozeDeal: async (item, context) => {
    try {
      const { removeAllSnoozeTags } = await import("@/lib/utils/task/snooze-tagging");
      const { getBrowserSupabaseClient } = await import("@west-tennessee-consulting/supabase");

      const updatedTags = removeAllSnoozeTags(
        (item as { tags?: string[] | null }).tags ?? undefined
      );
      const supabase = getBrowserSupabaseClient();

      const { error } = await supabase
        .from("deals")
        .update({ tags: updatedTags })
        .eq("id", item.id);

      if (error) {
        throw error;
      }

      context.onToast("Snooze cleared — follow-up reminders resumed", "success");
      void context.onRefresh?.();
    } catch (error) {
      context.onToast("Failed to clear snooze", "error");
      console.error("Error clearing deal snooze:", error);
    }
  },

  // Scheduled follow-up — set a specific future week to follow up (visible to
  // the team; distinct from snooze). Suppresses reminders until that week.
  setFollowUpDate: async (item, context) => {
    context.onOpenModal(MODAL_IDS.deals.scheduleFollowUp, {
      dealId: item.id,
      dealTitle: (item as { title?: string | null }).title ?? null,
      tags: (item as { tags?: string[] | null }).tags ?? [],
    });
  },

  clearFollowUpDate: async (item, context) => {
    try {
      const { clearScheduledFollowup } = await import(
        "@/lib/utils/task/scheduled-followup-tagging"
      );
      const { getBrowserSupabaseClient } = await import("@west-tennessee-consulting/supabase");

      const updatedTags = clearScheduledFollowup(
        (item as { tags?: string[] | null }).tags ?? undefined
      );
      const supabase = getBrowserSupabaseClient();

      const { error } = await supabase
        .from("deals")
        .update({ tags: updatedTags })
        .eq("id", item.id);

      if (error) {
        throw error;
      }

      context.onToast("Scheduled follow-up cleared", "success");
      void context.onRefresh?.();
    } catch (error) {
      context.onToast("Failed to clear scheduled follow-up", "error");
      console.error("Error clearing scheduled follow-up:", error);
    }
  },

  // Stage update actions
  markDealPaid: async (item, context) => {
    try {
      await context.onUpdateItem({
        stage: "paid" satisfies Deal_Stage,
      });
      context.onToast("Deal stage updated to Paid", "success");
    } catch (error) {
      context.onToast("Failed to update deal stage", "error");
      console.error("Error updating deal stage to Paid:", error);
    }
  },

  markDealInProgress: async (item, context) => {
    try {
      await context.onUpdateItem({
        stage: "in_progress" satisfies Deal_Stage,
      });
      context.onToast("Deal stage updated to In-Progress", "success");
    } catch (error) {
      context.onToast("Failed to update deal stage", "error");
      console.error("Error updating deal stage to In-Progress:", error);
    }
  },

  openCallContactPickerForDeal: async (item, context) => {
    context.onOpenModal("call-contact-picker", {
      item,
      entityId: item.id,
      companyId: item.company_id ?? null,
    });
  },
};
